import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '@ui/button.tsx';
import { cn } from '@r/utils/utils.ts';

type ActionBannerVariant = 'info' | 'warning';

const VARIANT_STYLES: Record<
  ActionBannerVariant,
  { container: string; text: string; button: string }
> = {
  info: {
    container: 'bg-purple-100 border-purple-200',
    text: 'text-purple-700',
    button: 'bg-purple-600 hover:bg-purple-700'
  },
  warning: {
    container: 'bg-amber-50 border-amber-200',
    text: 'text-amber-800',
    button: 'bg-amber-600 hover:bg-amber-700'
  }
};

interface ActionBannerAction {
  label: string;
  icon?: LucideIcon;
  // Soit une navigation, soit un callback
  to?: string;
  onClick?: () => void;
}

interface ActionBannerProps {
  icon: LucideIcon;
  children: ReactNode;
  action?: ActionBannerAction;
  variant?: ActionBannerVariant;
}

/*
 * Encart d'information pleine largeur avec une icône, un contenu libre et une action optionnelle.
 */
export const ActionBanner = ({
  icon: Icon,
  children,
  action,
  variant = 'info'
}: ActionBannerProps) => {
  const styles = VARIANT_STYLES[variant];
  const ActionIcon = action?.icon;

  const actionContent = action && (
    <>
      {action.label}
      {ActionIcon && <ActionIcon className="ml-2 w-4 h-4" />}
    </>
  );

  return (
    <div
      className={cn(
        'flex justify-between items-center p-4 w-full rounded-xl border',
        styles.container
      )}
    >
      <div className={cn('flex flex-1 items-center space-x-3', styles.text)}>
        <Icon className="w-5 h-5 shrink-0" />
        <div className="flex flex-col flex-1 space-y-1">{children}</div>
      </div>
      {action?.onClick && (
        <button
          type="button"
          onClick={action.onClick}
          className={cn(
            'inline-flex items-center px-4 py-2 ml-4 text-white rounded-lg transition-colors shrink-0',
            styles.button
          )}
        >
          {actionContent}
        </button>
      )}
      {action?.to && !action.onClick && (
        <Button
          asChild
          variant="default"
          className={cn('px-4 py-2 ml-4 text-white rounded-lg shrink-0', styles.button)}
        >
          <Link to={action.to}>{actionContent}</Link>
        </Button>
      )}
    </div>
  );
};
